/**
 * Staged creator-sheet import review — API client for the 8 endpoints under
 * /campaigns/{id}/creators/import* (spec §3.2, roster:import scope,
 * admin-only). Wire JSON is snake_case like every other endpoint in this
 * codebase; the FE types in @discovr/types are camelCase per the design's §4
 * — the wire->FE mapping happens explicitly here (toImportBatchRow etc.),
 * not via a generic key-casing utility.
 *
 * mapping / sheet-resolutions / advance-to-roster-review / roster-resolutions
 * all respond with the FULL ImportBatch (common.WriteData(w, status, batch)
 * on the BE — see admin_import_mapping_handler.go et al.), not just the row
 * snapshot, so every step transition (e.g. status flipping mapping ->
 * sheet_review) is available to the caller without a follow-up GET.
 */
import { apiClient } from '@discovr/common';
import type {
  ImportBatch,
  ImportBatchRow,
  ImportPreviewResult,
  ImportStatus,
  ExpectedField,
  SheetColumnPreview,
  RosterMatch,
  SheetResolution,
  RosterResolutionAction,
} from '@discovr/types';

// ── Wire shapes (snake_case JSON) ─────────────────────────────────────────
interface WireExpectedField {
  key: string;
  label: string;
  required: boolean;
  is_deliverable?: boolean;
}
interface WireSheetColumnPreview {
  header: string;
  sample_value: string;
  suggested_field: string;
}
interface WireRosterMatch {
  roster_entry_id: string;
  locked: boolean;
  status?: string;
  current_values: Record<string, string>;
}
// validation_errors/sheet_resolution/roster_match/roster_resolution are all
// `omitempty` on ImportBatchRow (import_batch.go) — absent, not zero-valued,
// on a row that hasn't reached that step yet.
interface WireImportBatchRow {
  row_number: number;
  raw: Record<string, string>;
  values: Record<string, string>;
  username_key: string;
  validation_errors?: Record<string, string>;
  duplicate_group_size: number;
  sheet_resolution?: SheetResolution;
  roster_match?: WireRosterMatch | null;
  roster_resolution?: RosterResolutionAction;
  roster_keep_fields?: string[];
}
interface WireImportPreviewResult {
  import_id: string;
  sheet_columns: WireSheetColumnPreview[];
  expected_fields: WireExpectedField[];
  row_count: number;
}
interface WireImportStatus {
  active_import_id?: string;
  expected_fields: WireExpectedField[];
}
// mapping/deflected_rows are also `omitempty` on ImportBatch.
interface WireImportBatch {
  id: string;
  campaign_id: string;
  uploaded_by: string;
  // omitempty on the Go struct — absent when the BE couldn't (or didn't try
  // to) resolve a display name for uploaded_by.
  uploaded_by_name?: string;
  status: string;
  file_name: string;
  headers: string[];
  mapping?: Record<string, string>;
  expected_fields: WireExpectedField[];
  // Not `omitempty` on the Go struct (import_batch.go), but a fresh batch at
  // status "mapping" (right after /preview, before mapping is submitted) is
  // constructed with no Rows set — a nil slice marshals to JSON `null`, not
  // `[]` or an absent key. See admin_import_preview_handler.go and the fixture
  // in admin_import_batch_get_handler_test.go.
  rows?: WireImportBatchRow[] | null;
  deflected_rows?: number[];
  created_at: string;
  updated_at: string;
  expires_at: string;
}
interface WireCommitResult {
  status: string;
  total_rows: number;
}

// ── Mappers ────────────────────────────────────────────────────────────────
function toExpectedField(w: WireExpectedField): ExpectedField {
  return { key: w.key, label: w.label, required: w.required, isDeliverable: w.is_deliverable };
}
function toSheetColumnPreview(w: WireSheetColumnPreview): SheetColumnPreview {
  return { header: w.header, sampleValue: w.sample_value, suggestedField: w.suggested_field };
}
function toRosterMatch(w: WireRosterMatch | null | undefined): RosterMatch | null {
  if (!w) return null;
  return {
    rosterEntryId: w.roster_entry_id,
    locked: w.locked,
    status: w.status,
    currentValues: w.current_values,
  };
}
function toImportBatchRow(w: WireImportBatchRow): ImportBatchRow {
  return {
    rowNumber: w.row_number,
    raw: w.raw,
    values: w.values,
    usernameKey: w.username_key,
    validationErrors: w.validation_errors ?? {},
    duplicateGroupSize: w.duplicate_group_size,
    sheetResolution: w.sheet_resolution ?? '',
    rosterMatch: toRosterMatch(w.roster_match),
    rosterResolution: w.roster_resolution ?? '',
    rosterKeepFields: w.roster_keep_fields ?? [],
  };
}
function toImportPreviewResult(w: WireImportPreviewResult): ImportPreviewResult {
  return {
    importId: w.import_id,
    sheetColumns: w.sheet_columns.map(toSheetColumnPreview),
    expectedFields: w.expected_fields.map(toExpectedField),
    rowCount: w.row_count,
  };
}
function toImportStatus(w: WireImportStatus): ImportStatus {
  return {
    activeImportId: w.active_import_id ?? null,
    expectedFields: w.expected_fields.map(toExpectedField),
  };
}
function toImportBatch(w: WireImportBatch): ImportBatch {
  return {
    id: w.id,
    campaignId: w.campaign_id,
    uploadedBy: w.uploaded_by,
    uploadedByName: w.uploaded_by_name ?? '',
    status: w.status as ImportBatch['status'],
    fileName: w.file_name,
    headers: w.headers,
    mapping: w.mapping ?? {},
    expectedFields: w.expected_fields.map(toExpectedField),
    rows: (w.rows ?? []).map(toImportBatchRow),
    deflectedRows: w.deflected_rows ?? [],
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    expiresAt: w.expires_at,
  };
}

const importPath = (campaignId: string, rest = '') =>
  `/campaigns/${encodeURIComponent(campaignId)}/creators/import${rest}`;

const previewImport = (campaignId: string, file: File): Promise<ImportPreviewResult> => {
  const form = new FormData();
  form.append('file', file);
  return apiClient
    .post<{ data: WireImportPreviewResult }>(importPath(campaignId, '/preview'), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => toImportPreviewResult(res.data.data));
};

const getImportStatus = (campaignId: string): Promise<ImportStatus> =>
  apiClient
    .get<{ data: WireImportStatus }>(importPath(campaignId, '/status'))
    .then((res) => toImportStatus(res.data.data));

const submitMapping = (
  campaignId: string,
  importId: string,
  mapping: Record<string, string>,
): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(
      importPath(campaignId, `/${encodeURIComponent(importId)}/mapping`),
      { mapping },
    )
    .then((res) => toImportBatch(res.data.data));

const submitSheetResolution = (
  campaignId: string,
  importId: string,
  payload: { rowNumber: number; action: 'keep' | 'drop' | 'fixed'; values?: Record<string, string> },
): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(
      importPath(campaignId, `/${encodeURIComponent(importId)}/sheet-resolutions`),
      { row_number: payload.rowNumber, action: payload.action, values: payload.values },
    )
    .then((res) => toImportBatch(res.data.data));

const stepBackImport = (campaignId: string, importId: string): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(importPath(campaignId, `/${encodeURIComponent(importId)}/back`), {})
    .then((res) => toImportBatch(res.data.data));

const advanceToRosterReview = (campaignId: string, importId: string): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(
      importPath(campaignId, `/${encodeURIComponent(importId)}/advance-to-roster-review`),
      {},
    )
    .then((res) => toImportBatch(res.data.data));

const submitRosterResolution = (
  campaignId: string,
  importId: string,
  payload: {
    rowNumber: number;
    action: 'keep_existing' | 'overwrite' | 'skip';
    /** Per-field merge option for action 'overwrite': each listed field key
     *  keeps the roster's current value instead of the sheet's. */
    keepFields?: string[];
  },
): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(
      importPath(campaignId, `/${encodeURIComponent(importId)}/roster-resolutions`),
      { row_number: payload.rowNumber, action: payload.action, keep_fields: payload.keepFields },
    )
    .then((res) => toImportBatch(res.data.data));

const commitImport = (
  campaignId: string,
  importId: string,
): Promise<{ status: string; totalRows: number }> =>
  apiClient
    .post<{ data: WireCommitResult }>(importPath(campaignId, `/${encodeURIComponent(importId)}/commit`), {})
    .then((res) => ({ status: res.data.data.status, totalRows: res.data.data.total_rows }));

const getImportBatch = (campaignId: string, importId: string): Promise<ImportBatch> =>
  apiClient
    .get<{ data: WireImportBatch }>(importPath(campaignId, `/${encodeURIComponent(importId)}`))
    .then((res) => toImportBatch(res.data.data));

const discardImport = (campaignId: string, importId: string): Promise<void> =>
  apiClient
    .delete(importPath(campaignId, `/${encodeURIComponent(importId)}`))
    .then(() => undefined);

export function createImportBatchApi() {
  return {
    previewImport,
    getImportStatus,
    submitMapping,
    submitSheetResolution,
    stepBackImport,
    advanceToRosterReview,
    submitRosterResolution,
    commitImport,
    getImportBatch,
    discardImport,
  };
}

export type ImportBatchApi = ReturnType<typeof createImportBatchApi>;
